import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { setAppLanguage } from "@/i18n";
import {
  useGetMe,
  useSeedDemo,
  useHealthCheck,
  useListMailAccounts,
  useDeleteMailAccount,
  useSyncMailAccount,
  useUpdateMailAccount,
  useGetBillingMe,
  useUpgradePlan,
  useBuyCredits,
  useListMyBillingEvents,
  getListMailAccountsQueryKey,
  getGetMeQueryKey,
  getGetBillingMeQueryKey,
  getListMyBillingEventsQueryKey,
  getHealthCheckQueryKey,
  type MailAccount,
  type BillingEventRow,
} from "@workspace/api-client-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useClerk, useUser } from "@clerk/react";
import { Input } from "@/components/ui/input";
import { getImpersonationHeaders } from "@/lib/impersonation";
import MailAccountForm from "@/components/mail-account-form";
import AddMailAccountModal from "@/components/add-mail-account-modal";
import MailTriageSettings from "@/components/mail-triage-settings";
import MailFullSyncControls from "@/components/mail-full-sync-controls";
import RgpdCard from "@/components/rgpd-card";
import AnalyzeNowCard from "@/components/analyze-now-card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  LogOut,
  Database,
  User,
  Sparkles,
  Settings as SettingsIcon,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  Plug,
  Mail,
  CreditCard,
  Zap,
} from "lucide-react";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";

const PREFS_KEY = "onenext.preferences.v1";

type Prefs = {
  language: "fr" | "en";
  defaultPriority: "low" | "medium" | "high";
  aiDetailLevel: "concis" | "standard" | "detaille";
};

const DEFAULT_PREFS: Prefs = {
  language: "fr",
  defaultPriority: "medium",
  aiDetailLevel: "standard",
};

function loadPrefs(): Prefs {
  if (typeof window === "undefined") return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<Prefs>;
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(prefs: Prefs) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // ignore storage errors
  }
}

export default function SettingsPage() {
  const { data: user, isLoading } = useGetMe();
  const { signOut } = useClerk();
  const { user: clerkUser } = useUser();
  const qc = useQueryClient();
  const seedDemo = useSeedDemo();
  const health = useHealthCheck({
    query: {
      queryKey: getHealthCheckQueryKey(),
      refetchInterval: 60_000,
      staleTime: 30_000,
    },
  });
  const { toast } = useToast();
  const { t } = useTranslation();

  const [editingName, setEditingName] = useState(false);
  const [firstNameDraft, setFirstNameDraft] = useState("");
  const [lastNameDraft, setLastNameDraft] = useState("");
  useEffect(() => {
    if (!editingName) {
      const parts = (user?.name ?? "").split(/\s+/);
      setFirstNameDraft(parts[0] ?? "");
      setLastNameDraft(parts.slice(1).join(" "));
    }
  }, [user?.name, editingName]);
  const saveName = useMutation({
    mutationFn: async ({ firstName, lastName }: { firstName: string; lastName: string }) => {
      const trimmedFirst = firstName.trim();
      if (!trimmedFirst) throw new Error("Le prénom ne peut pas être vide");
      const fullName = [trimmedFirst, lastName.trim()].filter(Boolean).join(" ");
      const res = await fetch("/api/me", {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...getImpersonationHeaders(),
        },
        body: JSON.stringify({ name: fullName }),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          if (typeof body?.error === "string") msg = body.error;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      // Best-effort sync to Clerk so the avatar / sign-in screen reflect it.
      if (clerkUser) {
        try {
          await clerkUser.update({ firstName: trimmedFirst, lastName: lastName.trim() || undefined });
        } catch {
          /* non-blocking */
        }
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Profil mis à jour" });
      setEditingName(false);
      qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
    },
    onError: (e) =>
      toast({
        title: "Erreur",
        description: (e as Error).message,
        variant: "destructive",
      }),
  });

  const [prefs, setPrefs] = useState<Prefs>(DEFAULT_PREFS);

  useEffect(() => {
    setPrefs(loadPrefs());
  }, []);

  const updatePrefs = (patch: Partial<Prefs>) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      savePrefs(next);
      return next;
    });
    if (patch.language) {
      const lang = patch.language;
      setAppLanguage(lang);
      fetch("/api/me", {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", ...getImpersonationHeaders() },
        body: JSON.stringify({ locale: lang }),
      }).catch(() => {});
    }
    toast({ title: t("settings.pref_saved") });
  };

  const handleSeedDemo = () => {
    seedDemo.mutate(undefined, {
      onSuccess: () => {
        toast({ title: "Données de démo chargées" });
      },
      onError: () => toast({ title: "Échec du chargement", variant: "destructive" }),
    });
  };

  const aiOnline = !health.isLoading && !health.error;

  return (
    <div className="w-full overflow-x-hidden p-4 md:p-8 max-w-2xl mx-auto pb-24 md:pb-8">
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-foreground mb-1">Paramètres</h1>
        <p className="text-muted-foreground">Gérez votre compte et vos préférences.</p>
      </header>

      <div className="space-y-6">
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" /> Profil
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            ) : (
              <div className="space-y-3">
                <div className="space-y-1">
                  {editingName ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="profile-firstname" className="text-xs uppercase tracking-wide text-muted-foreground">
                            Prénom
                          </Label>
                          <Input
                            id="profile-firstname"
                            value={firstNameDraft}
                            onChange={(e) => setFirstNameDraft(e.target.value)}
                            autoFocus
                            maxLength={60}
                            placeholder="Jean"
                            data-testid="profile-name-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="profile-lastname" className="text-xs uppercase tracking-wide text-muted-foreground">
                            Nom
                          </Label>
                          <Input
                            id="profile-lastname"
                            value={lastNameDraft}
                            onChange={(e) => setLastNameDraft(e.target.value)}
                            maxLength={60}
                            placeholder="Dupont"
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && firstNameDraft.trim()) {
                                saveName.mutate({ firstName: firstNameDraft, lastName: lastNameDraft });
                              } else if (e.key === "Escape") {
                                setEditingName(false);
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => saveName.mutate({ firstName: firstNameDraft, lastName: lastNameDraft })}
                          disabled={saveName.isPending || !firstNameDraft.trim()}
                          data-testid="profile-name-save"
                        >
                          {saveName.isPending && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                          Enregistrer
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingName(false)} disabled={saveName.isPending}>
                          Annuler
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Prénom &amp; Nom</Label>
                        <p className="font-medium text-foreground mt-0.5">
                          {user?.name || "Sans nom"}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingName(true)}
                        data-testid="profile-name-edit"
                      >
                        Modifier
                      </Button>
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Email
                  </Label>
                  <p className="text-sm text-foreground">{user?.email}</p>
                  <p className="text-xs text-muted-foreground">
                    L'email est géré par votre compte Clerk et ne se modifie pas ici.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-primary" /> {t("settings.preferences_section")}
            </CardTitle>
            <CardDescription>
              {t("settings.preferences_note")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="language">{t("settings.language_label")}</Label>
                <Select
                  value={prefs.language}
                  onValueChange={(v) => updatePrefs({ language: v as Prefs["language"] })}
                >
                  <SelectTrigger id="language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">{t("settings.lang_fr")}</SelectItem>
                    <SelectItem value="en">{t("settings.lang_en")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="default-priority">{t("settings.priority_label")}</Label>
                <Select
                  value={prefs.defaultPriority}
                  onValueChange={(v) =>
                    updatePrefs({ defaultPriority: v as Prefs["defaultPriority"] })
                  }
                >
                  <SelectTrigger id="default-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t("settings.priority_low")}</SelectItem>
                    <SelectItem value="medium">{t("settings.priority_medium")}</SelectItem>
                    <SelectItem value="high">{t("settings.priority_high")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-detail">{t("settings.ai_detail_label")}</Label>
              <Select
                value={prefs.aiDetailLevel}
                onValueChange={(v) =>
                  updatePrefs({ aiDetailLevel: v as Prefs["aiDetailLevel"] })
                }
              >
                <SelectTrigger id="ai-detail">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="concis">{t("settings.ai_concis")}</SelectItem>
                  <SelectItem value="standard">{t("settings.ai_standard")}</SelectItem>
                  <SelectItem value="detaille">{t("settings.ai_detaille")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <BillingCard />

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" /> Intelligence artificielle
            </CardTitle>
            <CardDescription>État de la connexion au moteur d'analyse.</CardDescription>
          </CardHeader>
          <CardContent>
            {health.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Vérification en cours…
              </div>
            ) : aiOnline ? (
              <div className="flex items-center gap-2 text-sm text-blue-600">
                <CheckCircle2 className="w-4 h-4" /> Service d'analyse opérationnel
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="w-4 h-4" /> Service d'analyse indisponible
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl opacity-60 pointer-events-none select-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" /> Données
              <span className="ml-auto text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Bientôt disponible</span>
            </CardTitle>
            <CardDescription>Actions liées à vos données OneNext.</CardDescription>
          </CardHeader>
        </Card>

        <MailAccountsCard />

        <AnalyzeNowCard />

        <Card className="rounded-2xl opacity-60 pointer-events-none select-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" /> Calendrier (ICS)
              <span className="ml-auto text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Bientôt disponible</span>
            </CardTitle>
            <CardDescription>
              Exportez vos événements vers Google, Apple ou Outlook Calendar.
            </CardDescription>
          </CardHeader>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plug className="w-5 h-5 text-primary" /> Intégrations
            </CardTitle>
            <CardDescription>
              Connectez OneNext à vos outils existants.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-xl border border-border bg-card/50">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">Google Calendar</p>
                  <p className="text-xs text-muted-foreground">
                    Synchronisez vos événements automatiquement.
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" disabled>
                Bientôt
              </Button>
            </div>
          </CardContent>
        </Card>

        <RgpdCard />

        <section className="pt-4">
          <Button
            variant="destructive"
            className="w-full sm:w-auto"
            onClick={() => signOut()}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Se déconnecter
          </Button>
        </section>
      </div>
    </div>
  );
}

// ── Stripe price IDs (created by scripts/src/seed-stripe-products.ts) ────
const STRIPE_PRICE_IDS: Record<string, { monthly: string; annual: string }> = {
  pro: {
    monthly: "price_1TTUI4KI7Qyd4dUtWcTsAPLu",
    annual: "price_1TTUI4KI7Qyd4dUtA64ADeUE",
  },
  unlimited: {
    monthly: "price_1TTUI5KI7Qyd4dUtxcxWS7IE",
    annual: "price_1TTUI5KI7Qyd4dUtML3MPYBg",
  },
};

function BillingCard() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useGetBillingMe();
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: getGetBillingMeQueryKey() });
    qc.invalidateQueries({ queryKey: getListMyBillingEventsQueryKey() });
  };

  const handleStripeCheckout = async (planId: string) => {
    const priceIds = STRIPE_PRICE_IDS[planId];
    if (!priceIds) return;
    const priceId = priceIds[billingPeriod];
    setCheckoutLoading(planId);
    try {
      const resp = await fetch(`${import.meta.env.BASE_URL}api/stripe/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ priceId }),
      });
      const json = await resp.json() as { url?: string; error?: string };
      if (json.url) {
        window.location.href = json.url;
      } else {
        toast({ title: json.error ?? "Erreur lors de la redirection vers le paiement", variant: "destructive" });
      }
    } catch {
      toast({ title: "Impossible de contacter le serveur de paiement", variant: "destructive" });
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleStripePortal = async () => {
    setPortalLoading(true);
    try {
      const resp = await fetch(`${import.meta.env.BASE_URL}api/stripe/portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const json = await resp.json() as { url?: string; error?: string };
      if (json.url) {
        window.location.href = json.url;
      } else {
        toast({ title: json.error ?? "Erreur lors de l'accès au portail", variant: "destructive" });
      }
    } catch {
      toast({ title: "Impossible d'accéder au portail de gestion", variant: "destructive" });
    } finally {
      setPortalLoading(false);
    }
  };

  const buy = useBuyCredits({
    mutation: {
      onSuccess: (s) => {
        toast({
          title: "Crédits ajoutés",
          description: `Solde : ${s.creditsRemaining} crédits`,
        });
        invalidate();
      },
      onError: () =>
        toast({ title: "Échec de l'achat", variant: "destructive" }),
    },
  });

  if (isLoading || !data) {
    return (
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" /> Forfait & crédits IA
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        </CardContent>
      </Card>
    );
  }

  const renews = data.planRenewsAt
    ? new Date(data.planRenewsAt).toLocaleDateString("fr-FR")
    : null;

  // When the plan is gifted the admin has offered this user their current plan
  // for free. The entire billing section is greyed out so they never see prompts
  // to pay, upgrade or buy credits — everything is already taken care of.
  if (data.planGifted) {
    return (
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" /> Forfait & crédits IA
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 p-4 rounded-xl border border-primary/30 bg-primary/5">
            <span className="text-2xl">🎁</span>
            <div>
              <p className="font-semibold text-foreground">Plan offert</p>
              <p className="text-sm text-muted-foreground">
                Votre accès est offert — aucune facturation ne vous sera demandée.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-card/50 opacity-60 select-none pointer-events-none">
            <div>
              <p className="text-sm text-muted-foreground">Forfait actuel</p>
              <p className="text-lg font-semibold text-foreground">{data.planLabel}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Crédits restants</p>
              <p className="text-2xl font-semibold text-foreground flex items-center gap-1 justify-end">
                <Zap className="w-5 h-5 text-primary" />
                {data.creditsUnlimited ? "∞" : data.creditsRemaining}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-primary" /> Forfait & crédits IA
        </CardTitle>
        <CardDescription>
          OneNext est gratuit pendant toute la durée de la beta.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
          <p className="text-sm font-semibold text-foreground">
            🎉 Gratuit pendant la beta
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Pendant la beta, toutes les fonctionnalités IA sont offertes
            jusqu'au 30 septembre 2026 — aucun forfait, aucune carte bancaire,
            aucune limite de crédits.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function formatEventDetails(ev: BillingEventRow): string | null {
  const p = ev.payload as Record<string, unknown> | null;
  if (!p) return null;
  switch (ev.type) {
    case "upgrade":
    case "plan_change": {
      const prev = typeof p.previousPlan === "string" ? p.previousPlan : null;
      const next =
        typeof p.newPlan === "string"
          ? p.newPlan
          : typeof p.plan === "string"
            ? p.plan
            : null;
      if (prev && next) return `${prev} → ${next}`;
      if (next) return `Forfait : ${next}`;
      return null;
    }
    case "credits_purchase": {
      const credits = typeof p.credits === "number" ? p.credits : null;
      const price = typeof p.priceEur === "number" ? p.priceEur : null;
      const parts: string[] = [];
      if (credits != null) parts.push(`+${credits} crédits`);
      if (price != null) parts.push(`${price} €`);
      return parts.join(" · ") || null;
    }
    case "admin_grant": {
      const kind = typeof p.kind === "string" ? p.kind : null;
      const amount = typeof p.amount === "number" ? p.amount : null;
      const reason = typeof p.reason === "string" ? p.reason : null;
      const parts: string[] = [];
      if (amount != null && kind === "credits") parts.push(`+${amount} crédits`);
      else if (amount != null && kind === "discount") parts.push(`Remise ${amount} €`);
      else if (amount != null) parts.push(String(amount));
      if (reason) parts.push(`« ${reason} »`);
      return parts.join(" · ") || null;
    }
    case "cap_change": {
      const cap = typeof p.dailyAiAnalysisCap === "number" ? p.dailyAiAnalysisCap : null;
      return cap != null ? `Quota quotidien : ${cap}` : null;
    }
    default:
      return null;
  }
}

function BillingHistory() {
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const { data, isLoading } = useListMyBillingEvents({ page, pageSize });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <div className="space-y-3" data-testid="billing-history">
      <p className="text-sm font-medium text-foreground">
        Historique de facturation
      </p>
      {isLoading || !data ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : data.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Aucun mouvement pour l'instant.
        </p>
      ) : (
        <>
          <ul className="divide-y divide-border rounded-xl border border-border bg-card/50">
            {data.rows.map((ev) => {
              const details = formatEventDetails(ev);
              const when = new Date(ev.createdAt).toLocaleString("fr-FR", {
                dateStyle: "medium",
                timeStyle: "short",
              });
              return (
                <li
                  key={ev.id}
                  className="px-4 py-3 flex items-start justify-between gap-3"
                  data-testid={`billing-event-${ev.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {ev.label}
                      {ev.byAdmin && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                          Support
                        </span>
                      )}
                    </p>
                    {details && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {details}
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground whitespace-nowrap">
                    {when}
                  </p>
                </li>
              );
            })}
          </ul>
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                Page {data.page} sur {totalPages} · {data.total} événement
                {data.total > 1 ? "s" : ""}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="billing-history-prev"
                >
                  Précédent
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="billing-history-next"
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MailAccountsCard() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useListMailAccounts();
  const accounts = (data ?? []) as MailAccount[];
  const [editing, setEditing] = useState<MailAccount | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const del = useDeleteMailAccount({
    mutation: {
      onSuccess: () => {
        toast({ title: "Compte supprimé" });
        qc.invalidateQueries({ queryKey: getListMailAccountsQueryKey() });
      },
    },
  });
  const sync = useSyncMailAccount({
    mutation: {
      onSuccess: () => toast({ title: "Synchronisation lancée" }),
      onError: (err: Error) =>
        toast({ title: "Synchro échouée", description: err.message, variant: "destructive" }),
    },
  });

  const updateAccount = useUpdateMailAccount({
    mutation: {
      onSuccess: () => qc.invalidateQueries({ queryKey: getListMailAccountsQueryKey() }),
      onError: () => toast({ title: "Erreur lors de la mise à jour", variant: "destructive" }),
    },
  });

  function handleSaved() {
    setEditing(null);
    setAddModalOpen(false);
    qc.invalidateQueries({ queryKey: getListMailAccountsQueryKey() });
  }

  return (
    <Card className="rounded-2xl" data-testid="card-mail-accounts">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary" /> Comptes mail
        </CardTitle>
        <CardDescription>
          Branche tes boîtes IMAP/SMTP. Les identifiants sont chiffrés.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        ) : (
          <>
            {accounts.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Aucun compte connecté pour l'instant.
              </p>
            ) : (
              <ul className="space-y-2">
                {accounts.map((a) => (
                  <li
                    key={a.id}
                    className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl border border-border bg-card/50 min-w-0 overflow-hidden"
                    data-testid={`account-row-${a.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground flex items-center gap-2">
                        {a.label}
                        {a.isDefault ? (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                            défaut
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {a.emailAddress} · {a.imapHost}
                      </p>
                      {a.lastSyncAt ? (
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Sync : {new Date(a.lastSyncAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      ) : null}
                      {a.lastError ? (
                        <p className="text-[11px] text-destructive mt-1">
                          {a.lastError}
                        </p>
                      ) : null}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`autosync-${a.id}`}
                            checked={a.autoSync}
                            onCheckedChange={(checked) =>
                              updateAccount.mutate({ id: a.id, data: { autoSync: checked } })
                            }
                            className="scale-90"
                          />
                          <Label htmlFor={`autosync-${a.id}`} className="text-xs text-muted-foreground cursor-pointer">
                            Sync automatique
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`analysis-${a.id}`}
                            checked={!a.analysisPaused}
                            onCheckedChange={(checked) =>
                              updateAccount.mutate({
                                id: a.id,
                                data: { analysisPaused: !checked },
                              })
                            }
                            className="scale-90"
                            data-testid={`switch-analysis-${a.id}`}
                          />
                          <Label
                            htmlFor={`analysis-${a.id}`}
                            className="text-xs text-muted-foreground cursor-pointer"
                          >
                            Analyse IA des mails
                          </Label>
                          {a.analysisPaused ? (
                            <span className="text-[10px] uppercase tracking-wide rounded bg-amber-100 text-amber-800 px-1.5 py-0.5">
                              en pause
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => sync.mutate({ id: a.id })}
                        disabled={sync.isPending}
                      >
                        Sync
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(a)}
                        data-testid={`button-edit-${a.id}`}
                      >
                        Modifier
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          if (confirm(`Supprimer le compte ${a.label} ?`)) {
                            del.mutate({ id: a.id });
                          }
                        }}
                      >
                        Supprimer
                      </Button>
                    </div>
                    <MailFullSyncControls accountId={a.id} />
                    <div className="basis-full">
                      <MailTriageSettings account={a} />
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {editing ? (
              <div className="border-t border-border pt-4">
                <h4 className="text-sm font-semibold mb-3">
                  Modifier {editing.label}
                </h4>
                <MailAccountForm
                  account={editing}
                  onSaved={handleSaved}
                  onCancel={() => setEditing(null)}
                />
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setAddModalOpen(true)}
                data-testid="button-add-account"
              >
                <Mail className="w-4 h-4 mr-2" />
                Ajouter un compte mail
              </Button>
            )}

            <AddMailAccountModal
              open={addModalOpen}
              onOpenChange={setAddModalOpen}
              onSaved={handleSaved}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
